import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import sound from '../Assets/Shino-Ad-shortenedd-FINAL.wav';
import networkReconnectSound from '../Assets/network_reconnect.mp3';
import shino_logo from '../Assets/shino_logo.png';

/**
 * 🔥 ENTERPRISE-GRADE WebSocket + Audio Notification System
 * 
 * Production-ready implementation with:
 * - WebSocket reconnection with exponential backoff
 * - Heartbeat/ping-pong system
 * - Background tab support (setTimeout-based)
 * - Page Visibility API integration
 * - SessionStorage persistence
 * - Comprehensive error handling
 * - Auto-reload on max connection failures
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.socketUrl - WebSocket URL (required)
 * @param {string|Audio} options.soundFile - Audio file path (default: imported sound)
 * @param {number} options.throttleMs - Audio throttle duration (default: 2000)
 * @param {Object} options.toastRef - PrimeReact Toast ref (optional)
 * @param {string} options.toastMessage - Toast message (default: 'New order received!')
 * @param {number} options.maxReconnectAttempts - Max reconnection attempts (default: 10)
 * @param {number} options.heartbeatInterval - Heartbeat interval in ms (default: 30000)
 * @param {number} options.heartbeatTimeout - Heartbeat timeout in ms (default: 10000)
 * @param {Function} options.onNewOrder - Callback when new order is detected (optional)
 * @returns {Object} - { playOrderSound, ready, error, isConnected, reconnect }
 */

// Browser detection for optimizations
const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isSafari = /^((?!chrome|android|edg|edge|opera|opr).)*safari/i.test(userAgent);
  const isChrome = /chrome/i.test(userAgent) && !/edg|edge|opera|opr/i.test(userAgent);
  const isFirefox = /firefox/i.test(userAgent);
  const isEdge = /edg|edge/i.test(userAgent);
  const isIOS = /iphone|ipad|ipod/i.test(userAgent);
  const isMac = /macintosh|mac os x/i.test(userAgent);
  
  return { isSafari, isChrome, isFirefox, isEdge, isIOS, isMac };
};

const useOrderSoundNotification = ({
  socketUrl, // REQUIRED: WebSocket URL
  soundFile = sound,
  throttleMs = 2000,
  toastRef = null,
  toastMessage = 'New order received!',
  maxReconnectAttempts = 10,
  heartbeatInterval = 30000, // 30 seconds
  heartbeatTimeout = 10000, // 10 seconds
  enableHeartbeat = false, // Disable heartbeat by default if server doesn't support pong
  onNewOrder, // Callback when new order is detected (optional)
  getPendingOrdersCount = null, // Callback to get current pending orders count (optional)
  userRole = null, // User role to check if notifications should be shown
} = {}) => {
  // Audio state
  const [audioReady, setAudioReady] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const lastPlayTimeRef = useRef(0);
  const isPlayingRef = useRef(false);
  const audioUnlockedRef = useRef(false);
  const activeWebAudioSourcesRef = useRef(new Set());
  const pendingOrdersBatchRef = useRef([]); // Track pending orders in a batch
  const batchTimeoutRef = useRef(null); // Timeout for batching orders
  const BATCH_WINDOW_MS = 2000; // 2 seconds window to batch simultaneous orders
  
  // Network disconnect audio
  const networkDisconnectAudioRef = useRef(null);
  const networkDisconnectPlayingRef = useRef(false);

  // WebSocket state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const lastPongTimeRef = useRef(Date.now());
  const isTabVisibleRef = useRef(!document.hidden);
  const reloadTimeoutRef = useRef(null);
  const consecutiveHeartbeatFailuresRef = useRef(0);
  const MAX_HEARTBEAT_FAILURES = 3; // Only close after 3 consecutive failures
  const networkErrorToastRef = useRef(null); // Reference to network error toast to clear it on reconnect
  const networkErrorToastKeyRef = useRef(null); // Store the toast key for reliable clearing
  const hideCloseButtonIntervalRef = useRef(null); // Interval to continuously hide close button

  // Enterprise-grade features: BroadcastChannel and Web Notifications
  const broadcastChannelRef = useRef(null); // BroadcastChannel for multi-tab coordination
  const notificationPermissionRef = useRef(null); // Track notification permission status
  const isActiveTabRef = useRef(true); // Track if this tab is the active one for sound
  const playOrderSoundRef = useRef(null); // Ref to playOrderSound for BroadcastChannel

  // Constants
  const STORAGE_KEY = 'orderSoundNotification_unlocked';
  const BROADCAST_CHANNEL_NAME = 'order_notifications_channel';
  const INITIAL_BACKOFF = 5000; // 5 seconds
  const MAX_BACKOFF = 30000; // 30 seconds
  const browserInfo = useRef(detectBrowser());

  /**
   * Save audio unlock state to sessionStorage
   */
  const saveUnlockState = useCallback((unlocked) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(unlocked));
    } catch (err) {
      console.warn('⚠️ Failed to save unlock state:', err);
    }
  }, []);

  /**
   * Load audio unlock state from sessionStorage
   */
  const loadUnlockState = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored === 'true') {
        audioUnlockedRef.current = true;
        console.log('✅ Audio unlock state restored from sessionStorage');
        return true;
      }
    } catch (err) {
      console.warn('⚠️ Failed to load unlock state:', err);
    }
    return false;
  }, []);

  /**
   * Request notification permission (Web Notifications API)
   */
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('⚠️ Web Notifications API not supported');
      return false;
    }

    if (Notification.permission === 'granted') {
      notificationPermissionRef.current = 'granted';
      return true;
    }

    if (Notification.permission === 'denied') {
      notificationPermissionRef.current = 'denied';
      console.warn('⚠️ Notification permission denied');
      return false;
    }

    // Request permission
    try {
      const permission = await Notification.requestPermission();
      notificationPermissionRef.current = permission;
      return permission === 'granted';
    } catch (err) {
      console.warn('⚠️ Error requesting notification permission:', err);
      return false;
    }
  }, []);

  /**
   * Show browser notification (for background tabs)
   */
  const showBrowserNotification = useCallback((title, options = {}) => {
    if (!('Notification' in window)) {
      return null;
    }

    if (notificationPermissionRef.current !== 'granted') {
      // Try to request permission if not yet requested
      requestNotificationPermission().then(granted => {
        if (granted) {
          showBrowserNotification(title, options);
        }
      });
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: shino_logo, // Default icon
        badge: shino_logo,
        tag: 'order-notification', 
        requireInteraction: false,
        silent: false, // Allow sound
        ...options
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (err) {
      console.warn('⚠️ Error showing browser notification:', err);
      return null;
    }
  }, [requestNotificationPermission]);

  /**
   * Initialize BroadcastChannel for multi-tab coordination
   * Note: playOrderSound is passed as a parameter to avoid circular dependency
   */
  const initBroadcastChannel = useCallback((playOrderSoundFn) => {
    if (!('BroadcastChannel' in window)) {
      console.warn('⚠️ BroadcastChannel not supported');
      return;
    }

    try {
      broadcastChannelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      
      // Listen for messages from other tabs
      broadcastChannelRef.current.onmessage = (event) => {
        const { type, data } = event.data;

        if (type === 'NEW_ORDER') {
          // Another tab detected a new order
          // ALWAYS play sound regardless of tab visibility (active or inactive)
          const isTabVisible = !document.hidden && isTabVisibleRef.current;
          
          // Play sound (works in both active and inactive tabs)
          if (playOrderSoundRef.current) {
            console.log('📢 Received NEW_ORDER from another tab - playing sound');
            playOrderSoundRef.current();
          } else if (playOrderSoundFn) {
            console.log('📢 Received NEW_ORDER from another tab - playing sound');
            playOrderSoundFn();
          }
          
          // Also show browser notification if tab is inactive (additional feedback)
          if (!isTabVisible) {
            console.log('📢 Tab is inactive - showing browser notification (sound also playing)');
            showBrowserNotification('🆕 New Order Received!', {
              body: `You have ${data.count || 1} new order(s)`,
              tag: 'new-order'
            });
          }
        } else if (type === 'TAB_ACTIVE') {
          // Another tab claims to be active
          // Only the FIRST tab to send this becomes active
          if (isActiveTabRef.current) {
            // We're already active, ignore
            return;
          }
          // This tab is now inactive
          isActiveTabRef.current = false;
          console.log('📢 Another tab is active - this tab will only show notifications');
        }
      };

      // Announce this tab as active when it becomes visible
      const announceActive = () => {
        if (!document.hidden && broadcastChannelRef.current) {
          isActiveTabRef.current = true;
          broadcastChannelRef.current.postMessage({
            type: 'TAB_ACTIVE'
          });
          console.log('📢 This tab is now active for sound notifications');
        }
      };

      // Announce on visibility change
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          announceActive();
        }
      });

      // Initial announcement
      announceActive();

      console.log('✅ BroadcastChannel initialized');
    } catch (err) {
      console.warn('⚠️ Error initializing BroadcastChannel:', err);
    }
  }, [showBrowserNotification]);

  /**
   * Broadcast new order to other tabs
   */
  const broadcastNewOrder = useCallback((count = 1) => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: 'NEW_ORDER',
        data: { count }
      });
      console.log(`📢 Broadcasted NEW_ORDER to other tabs (count: ${count})`);
    }
  }, []);

  /**
   * Show toast notification
   * @param {string} severity - Toast severity (error, warn, info, success)
   * @param {string} summary - Toast summary/title
   * @param {string} detail - Toast detail message
   * @param {number} life - Toast lifetime in ms (0 = sticky, won't auto-hide)
   * @param {boolean} persist - If true, toast persists until manually cleared
   * @param {boolean} closable - If false, toast cannot be dismissed (default: true)
   * @returns {Object|null} - Toast instance reference or null
   */
  const showToast = useCallback((severity, summary, detail, life = 3000, persist = false, closable = true) => {
    // Skip toast if user role is 'user'
    if (userRole === 'user') {
      return null;
    }
    if (toastRef?.current) {
      try {
        // If persist is true, use 0 or very large number for life
        const toastLife = persist ? 0 : life;
        // PrimeReact Toast options
        const toastOptions = { 
          severity, 
          summary, 
          detail, 
          life: toastLife
        };
        
        // Try to set closable if supported (PrimeReact may or may not support this)
        if (closable === false) {
          toastOptions.closable = false;
        }
        
        const toastInstance = toastRef.current.show(toastOptions);
        
        // If closable is false, hide the close button via CSS after toast is shown
        if (closable === false) {
          // Use multiple timeouts to catch the toast after it's rendered
          const hideCloseButton = () => {
            const toastContainer = document.querySelector('.p-toast');
            if (toastContainer) {
              // Find the most recent toast message (last one)
              const toastMessages = toastContainer.querySelectorAll('.p-toast-message');
              if (toastMessages.length > 0) {
                const lastToast = toastMessages[toastMessages.length - 1];
                // Try multiple selectors for close button
                const closeButton = lastToast.querySelector('.p-toast-icon-close, .p-toast-message-close, .p-toast-close-icon, button[aria-label="close"]');
                if (closeButton) {
                  closeButton.style.display = 'none';
                  closeButton.style.visibility = 'hidden';
                  closeButton.style.pointerEvents = 'none';
                  closeButton.style.opacity = '0';
                  closeButton.setAttribute('disabled', 'true');
                  console.log('✅ Close button hidden for network error toast');
                } else {
                  // If button not found, try to hide parent close container
                  const closeContainer = lastToast.querySelector('[class*="close"]');
                  if (closeContainer) {
                    closeContainer.style.display = 'none';
                  }
                }
              }
            }
          };
          
          // Try multiple times to ensure we catch it
          setTimeout(hideCloseButton, 50);
          setTimeout(hideCloseButton, 100);
          setTimeout(hideCloseButton, 200);
        }
        
        return toastInstance;
      } catch (err) {
        console.warn('⚠️ Toast failed:', err);
        return null;
      }
    }
    return null;
  }, [toastRef, userRole]);

  /**
   * Clear a specific toast by key
   */
  const clearToast = useCallback((key) => {
    if (toastRef?.current && key) {
      try {
        toastRef.current.remove(key);
      } catch (err) {
        console.warn('⚠️ Failed to clear toast:', err);
      }
    }
  }, [toastRef]);

  /**
   * Continuously hide close button for network error toast and ensure it stays visible
   */
  const startHidingCloseButton = useCallback(() => {
    // Clear any existing interval
    if (hideCloseButtonIntervalRef.current) {
      clearInterval(hideCloseButtonIntervalRef.current);
    }

    // Continuously check and hide close button every 100ms
    // Also ensure toast stays visible if network is still offline
    // ONLY runs when network is offline - stops automatically when online
    hideCloseButtonIntervalRef.current = setInterval(() => {
      // Only run this logic if network is actually offline
      if (navigator.onLine) {
        // Network is online, stop the interval (will be cleared when WebSocket connects)
        return;
      }

      const toastContainer = document.querySelector('.p-toast');
      let networkErrorToastExists = false;
      
      if (toastContainer) {
        const toastMessages = toastContainer.querySelectorAll('.p-toast-message');
        toastMessages.forEach(msg => {
          // Check if this is the network error toast (by checking message content)
          // IMPORTANT: Only target toasts with specific network error messages
          const summary = msg.querySelector('.p-toast-summary');
          const detail = msg.querySelector('.p-toast-detail');
          const isNetworkErrorToast = summary && detail && 
              (summary.textContent.includes('No Internet Connection') || 
               summary.textContent.includes('Internet connection'));
          
          if (isNetworkErrorToast) {
            networkErrorToastExists = true;
            
            // Hide close button ONLY for network error toast
            // Use specific selectors to avoid affecting other toasts
            const selectors = [
              '.p-toast-icon-close',
              '.p-toast-message-close',
              '.p-toast-close-icon',
              'button[aria-label="close"]',
              'button[aria-label="Close"]'
            ];
            
            selectors.forEach(selector => {
              const closeButton = msg.querySelector(selector);
              if (closeButton) {
                // Only hide if it's actually a close button (not other buttons)
                const isCloseButton = closeButton.closest('.p-toast-message') === msg &&
                                     (closeButton.classList.contains('p-toast-icon-close') ||
                                      closeButton.classList.contains('p-toast-message-close') ||
                                      closeButton.getAttribute('aria-label')?.toLowerCase().includes('close'));
                
                if (isCloseButton) {
                  closeButton.style.display = 'none';
                  closeButton.style.visibility = 'hidden';
                  closeButton.style.pointerEvents = 'none';
                  closeButton.style.opacity = '0';
                  closeButton.setAttribute('disabled', 'true');
                  closeButton.setAttribute('aria-hidden', 'true');
                }
              }
            });
          }
        });
      }
      
      // If network is still offline but toast disappeared, re-show it
      if (!navigator.onLine && !networkErrorToastExists) {
        console.log('⚠️ Network error toast disappeared but network still offline - re-showing...');
        // Reset refs to allow re-showing
        networkErrorToastRef.current = null;
        networkErrorToastKeyRef.current = null;
        // Re-show the toast
        networkErrorToastRef.current = showToast(
          'error', 
          'No Internet Connection', 
          'No internet connection. Please check your network. This message will disappear when connection is restored.',
          0,
          true,
          false
        );
        networkErrorToastKeyRef.current = networkErrorToastRef.current;
      }
    }, 100); // Check every 100ms
  }, [showToast]);

  /**
   * Stop hiding close button
   */
  const stopHidingCloseButton = useCallback(() => {
    if (hideCloseButtonIntervalRef.current) {
      clearInterval(hideCloseButtonIntervalRef.current);
      hideCloseButtonIntervalRef.current = null;
    }
  }, []);

  /**
   * Unlock audio (required for browser autoplay)
   */
  const unlockAudio = useCallback(() => {
    if (!audioRef.current || audioUnlockedRef.current) return;

    try {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioUnlockedRef.current = true;
            saveUnlockState(true);
            console.log('✅ Audio unlocked successfully');
          })
          .catch((err) => {
            console.warn('⚠️ Audio unlock failed:', err);
            // Try Web Audio API fallback
            // Note: AudioContext can only be created/resumed after user interaction
            // So we'll create it here but it may be suspended until user interacts
            if (!audioContextRef.current) {
              try {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                if (AudioContext) {
                  // Create AudioContext (will be suspended until user interaction)
                  audioContextRef.current = new AudioContext();
                  console.log('✅ AudioContext created (state:', audioContextRef.current.state, ')');
                  
                  // Try to resume if not suspended (only works if user has interacted)
                  if (audioContextRef.current.state === 'suspended') {
                    // Attempt resume (will only work if called from user interaction context)
                    audioContextRef.current.resume().then(() => {
                      console.log('✅ AudioContext resumed successfully');
                      audioUnlockedRef.current = true;
                      saveUnlockState(true);
                    }).catch(resumeErr => {
                      // Suppress autoplay warnings - they're expected
                      if (resumeErr.name !== 'NotAllowedError' && !resumeErr.message.includes('user gesture')) {
                        console.log('ℹ️ AudioContext resume deferred:', resumeErr.message);
                      } else {
                        console.log('ℹ️ AudioContext resume deferred (will resume on next user interaction)');
                      }
                      // AudioContext will remain suspended until user interacts
                      // This is expected behavior - it will be resumed on next user gesture
                    });
                  } else {
                    // Already running
                    audioUnlockedRef.current = true;
                    saveUnlockState(true);
                  }
                }
              } catch (audioContextErr) {
                console.warn('⚠️ AudioContext initialization failed:', audioContextErr);
              }
            } else {
              // AudioContext already exists - try to resume if suspended
              if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume().then(() => {
                  console.log('✅ Existing AudioContext resumed');
                  audioUnlockedRef.current = true;
                  saveUnlockState(true);
                }).catch(resumeErr => {
                  // Suppress autoplay warnings - they're expected
                  if (resumeErr.name !== 'NotAllowedError' && !resumeErr.message.includes('user gesture')) {
                    console.log('ℹ️ AudioContext resume deferred:', resumeErr.message);
                  } else {
                    console.log('ℹ️ AudioContext resume deferred (will resume on next user interaction)');
                  }
                });
              }
            }
          });
      } else {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioUnlockedRef.current = true;
        saveUnlockState(true);
      }
    } catch (err) {
      console.warn('⚠️ Error during audio unlock:', err);
    }
  }, [saveUnlockState]);

  /**
   * Initialize audio system
   */
  useEffect(() => {
    try {
      const audio = typeof soundFile === 'string' ? new Audio(soundFile) : new Audio(soundFile);
      audio.preload = 'auto';
      audio.volume = 1.0;
      audio.loop = false;
      audio.muted = false;

      if (browserInfo.current.isSafari) {
        audio.crossOrigin = 'anonymous';
      }

      const handleCanPlay = () => {
        console.log('✅ Order sound loaded and ready');
        setAudioReady(true);
        setAudioError(null);
        // onNewOrder();
      };

      const handleError = (err) => {
        console.error('❌ Failed to load order sound:', err);
        setAudioError(err);
        setAudioReady(false);
      };

      const handleEnded = () => {
        isPlayingRef.current = false;
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
      };

      audio.addEventListener('canplaythrough', handleCanPlay);
      audio.addEventListener('error', handleError);
      audio.addEventListener('ended', handleEnded);

      audioRef.current = audio;
      audio.load();

      // Load unlock state from sessionStorage
      loadUnlockState();

      // Initialize Web Audio API for background playback
      const initWebAudioAPI = async () => {
        try {
          const AudioContext = window.AudioContext || window.webkitAudioContext;
          if (AudioContext && !audioContextRef.current) {
            audioContextRef.current = new AudioContext();
            try {
              const response = await fetch(soundFile);
              const arrayBuffer = await response.arrayBuffer();
              audioBufferRef.current = await audioContextRef.current.decodeAudioData(arrayBuffer);
              console.log('✅ Audio loaded into Web Audio API buffer');
            } catch (bufferError) {
              console.warn('⚠️ Failed to load audio buffer:', bufferError);
            }
          }
        } catch (webAudioError) {
          console.warn('⚠️ Web Audio API initialization failed:', webAudioError);
        }
      };
      initWebAudioAPI();

      // Set up user interaction listeners for audio unlock
      const handleUserInteraction = () => {
        if (!audioUnlockedRef.current) {
          console.log('👆 User interaction detected - unlocking audio...');
          unlockAudio();
        }
      };

      const events = ['click', 'touchstart', 'keydown', 'mousedown', 'mouseup', 'touchend'];
      const targets = [window, document, document.body].filter(Boolean);

      targets.forEach(target => {
        events.forEach(eventType => {
          target.addEventListener(eventType, handleUserInteraction, {
            once: false,
            passive: true,
            capture: browserInfo.current.isSafari
          });
        });
      });

      // Cleanup
      return () => {
        audio.removeEventListener('canplaythrough', handleCanPlay);
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('ended', handleEnded);

        targets.forEach(target => {
          events.forEach(eventType => {
            target.removeEventListener(eventType, handleUserInteraction, {
              capture: browserInfo.current.isSafari
            });
          });
        });

        activeWebAudioSourcesRef.current.forEach(source => {
          try {
            source.stop();
            source.disconnect();
          } catch (err) {
            console.warn('⚠️ Error stopping Web Audio source:', err);
          }
        });
        activeWebAudioSourcesRef.current.clear();

        if (audioRef.current) {
          try {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          } catch (err) {
            console.warn('⚠️ Error stopping audio:', err);
          }
          audioRef.current = null;
        }

        if (audioContextRef.current) {
          try {
            if (audioContextRef.current.state !== 'closed') {
              audioContextRef.current.close().catch(console.warn);
            }
          } catch (err) {
            console.warn('⚠️ Error closing AudioContext:', err);
          }
          audioContextRef.current = null;
        }

      audioBufferRef.current = null;
      setAudioReady(false);
      isPlayingRef.current = false;
    };
  } catch (err) {
    console.error('❌ Error initializing audio:', err);
    setAudioError(err);
    setAudioReady(false);
  }
}, [soundFile, unlockAudio, loadUnlockState]);

  /**
   * Initialize network disconnect audio
   */
  useEffect(() => {
    try {
      const networkAudio = new Audio(networkReconnectSound);
      networkAudio.preload = 'auto';
      networkAudio.volume = 1.0;
      networkAudio.loop = true; // Loop continuously until network comes back
      networkAudio.muted = false;

      if (browserInfo.current.isSafari) {
        networkAudio.crossOrigin = 'anonymous';
      }

      const handleCanPlay = () => {
        console.log('✅ Network disconnect audio loaded and ready');
      };

      const handleError = (err) => {
        console.warn('⚠️ Failed to load network disconnect audio:', err);
      };

      const handleEnded = () => {
        networkDisconnectPlayingRef.current = false;
        if (networkDisconnectAudioRef.current) {
          networkDisconnectAudioRef.current.currentTime = 0;
        }
      };

      networkAudio.addEventListener('canplaythrough', handleCanPlay);
      networkAudio.addEventListener('error', handleError);
      networkAudio.addEventListener('ended', handleEnded);

      networkDisconnectAudioRef.current = networkAudio;
      networkAudio.load();

      // Cleanup
      return () => {
        networkAudio.removeEventListener('canplaythrough', handleCanPlay);
        networkAudio.removeEventListener('error', handleError);
        networkAudio.removeEventListener('ended', handleEnded);

        if (networkDisconnectAudioRef.current) {
          try {
            networkDisconnectAudioRef.current.pause();
            networkDisconnectAudioRef.current.currentTime = 0;
          } catch (err) {
            console.warn('⚠️ Error stopping network disconnect audio:', err);
          }
          networkDisconnectAudioRef.current = null;
        }

        networkDisconnectPlayingRef.current = false;
      };
    } catch (err) {
      console.warn('⚠️ Error initializing network disconnect audio:', err);
    }
  }, []);

  /**
   * Calculate exponential backoff delay
   */
  const getBackoffDelay = useCallback((attempt) => {
    const delay = Math.min(INITIAL_BACKOFF * Math.pow(2, attempt), MAX_BACKOFF);
    return delay;
  }, []);

  /**
   * Clear heartbeat timers
   */
  const clearHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearTimeout(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  /**
   * Send ping and schedule next heartbeat (using setTimeout for background safety)
   */
  const sendPing = useCallback(() => {
    if (!enableHeartbeat) {
      // Heartbeat disabled, don't send pings
      return;
    }

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      try {
        const pingTime = Date.now();
        socketRef.current.send(JSON.stringify({ type: 'ping' }));
        console.log('💓 Sent ping');

        // Set timeout for pong response
        heartbeatTimeoutRef.current = setTimeout(() => {
          const timeSincePing = Date.now() - pingTime;
          if (timeSincePing > heartbeatTimeout) {
            consecutiveHeartbeatFailuresRef.current += 1;
            console.warn(`⚠️ Heartbeat timeout - no pong received (${consecutiveHeartbeatFailuresRef.current}/${MAX_HEARTBEAT_FAILURES})`);
            
            // Only close connection after multiple consecutive failures
            if (consecutiveHeartbeatFailuresRef.current >= MAX_HEARTBEAT_FAILURES) {
              console.error('❌ Multiple heartbeat failures - treating as dead connection');
              if (socketRef.current) {
                socketRef.current.close();
              }
              clearHeartbeat();
              consecutiveHeartbeatFailuresRef.current = 0;
            }
            // If not enough failures, just continue - don't close connection
          }
        }, heartbeatTimeout);
      } catch (err) {
        console.error('❌ Error sending ping:', err);
        clearHeartbeat();
      }
    } else {
      clearHeartbeat();
    }
  }, [heartbeatTimeout, clearHeartbeat, enableHeartbeat]);

  /**
   * Start heartbeat system (using setTimeout chain for background safety)
   */
  const startHeartbeat = useCallback(() => {
    clearHeartbeat();

    // Schedule first ping immediately, then chain with setTimeout
    const scheduleNextPing = () => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        sendPing();
        // Schedule next ping using setTimeout (background-safe)
        heartbeatIntervalRef.current = setTimeout(scheduleNextPing, heartbeatInterval);
      } else {
        clearHeartbeat();
      }
    };

    // Start the chain
    scheduleNextPing();
  }, [heartbeatInterval, clearHeartbeat, sendPing]);

  /**
   * Connect WebSocket with enterprise-grade reconnection
   */
  const connectWebSocket = useCallback(() => {
    if (!socketUrl) {
      console.error('❌ WebSocket URL is required');
      return;
    }

    // Check network status
    if (!navigator.onLine) {
      console.error('❌ No internet connection');
      setConnectionError('No internet connection');
      // Show persistent toast that won't hide until network reconnects (non-dismissible)
      if (!networkErrorToastRef.current) {
        // Only show if not already showing
        networkErrorToastRef.current = showToast(
          'error', 
          'No Internet Connection', 
          'No internet connection. Please check your network. This message will disappear when connection is restored.',
          0, // 0 = sticky, won't auto-hide
          true, // persist until cleared
          false // NOT closable - user cannot dismiss it
        );
        networkErrorToastKeyRef.current = networkErrorToastRef.current;
        // Start continuously hiding close button
        startHidingCloseButton();
        console.log('📴 Network error toast shown (non-dismissible)');
      }
      scheduleReconnect();
      return;
    }

    // Close existing connection
    if (socketRef.current) {
      try {
        socketRef.current.close();
      } catch (err) {
        console.warn('⚠️ Error closing existing WebSocket:', err);
      }
    }

    console.log('🔌 Attempting to connect WebSocket...');

    try {
      socketRef.current = new WebSocket(`wss://${socketUrl}/ws/admin/orders/`);

      socketRef.current.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        
        reconnectAttemptsRef.current = 0; // Reset attempts on success
        consecutiveHeartbeatFailuresRef.current = 0; // Reset heartbeat failures
        setConnectionError(null);
        setIsConnected(true);
        clearHeartbeat(); // Clear any existing heartbeat
        if (enableHeartbeat) {
          startHeartbeat(); // Start heartbeat system only if enabled
        }

        // Clear network error toast ONLY when WebSocket actually connects
        // This ensures toast stays visible until connection is truly restored
        if (networkErrorToastRef.current || networkErrorToastKeyRef.current) {
          console.log('✅ WebSocket connected - clearing network error toast');
          stopHidingCloseButton(); // Stop hiding close button
          
          // Clear toast by key if available
          if (networkErrorToastKeyRef.current) {
            try {
              clearToast(networkErrorToastKeyRef.current);
            } catch (err) {
              console.warn('⚠️ Error clearing toast by key:', err);
            }
          }
          
          // Also try clearing by ref
          if (networkErrorToastRef.current) {
            try {
              clearToast(networkErrorToastRef.current);
            } catch (err) {
              console.warn('⚠️ Error clearing toast by ref:', err);
            }
          }
          
          // Force remove all network error toasts from DOM
          setTimeout(() => {
            const toastContainer = document.querySelector('.p-toast');
            if (toastContainer) {
              const toastMessages = toastContainer.querySelectorAll('.p-toast-message');
              toastMessages.forEach(msg => {
                const summary = msg.querySelector('.p-toast-summary');
                if (summary && 
                    (summary.textContent.includes('No Internet Connection') || 
                     summary.textContent.includes('Internet connection'))) {
                  msg.remove(); // Remove from DOM
                }
              });
            }
          }, 100);
          
          networkErrorToastRef.current = null;
          networkErrorToastKeyRef.current = null;
        }

        // Send initial request
        try {
          socketRef.current.send(JSON.stringify({ source: 'request.list' }));
        } catch (err) {
          console.error('❌ Error sending initial message:', err);
        }

        // Show success toast if reconnected
        if (reconnectAttemptsRef.current > 0) {
          showToast('success', 'Connection Restored', 'WebSocket reconnected successfully. Order notifications are active.');
        }

        // Clear reload timeout if exists
        if (reloadTimeoutRef.current) {
          clearTimeout(reloadTimeoutRef.current);
          reloadTimeoutRef.current = null;
        }
      };

      socketRef.current.onmessage = (event) => {
        try {
          const parsedMessage = JSON.parse(event.data);

          // Handle pong response
          if (parsedMessage.type === 'pong') {
            lastPongTimeRef.current = Date.now();
            consecutiveHeartbeatFailuresRef.current = 0; // Reset failure counter on successful pong
            if (heartbeatTimeoutRef.current) {
              clearTimeout(heartbeatTimeoutRef.current);
              heartbeatTimeoutRef.current = null;
            }
            console.log('💚 Received pong');
            return;
          }

          // Handle new order
          if (parsedMessage.message === 'Recieved new Order') {
            console.log('🆕 New order detected');
            
            // Add to batch for simultaneous orders
            pendingOrdersBatchRef.current.push({
              timestamp: Date.now(),
              order: parsedMessage
            });
            
            // Clear existing batch timeout
            if (batchTimeoutRef.current) {
              clearTimeout(batchTimeoutRef.current);
              batchTimeoutRef.current = null;
            }
            
            // Process batch after a short delay (to catch simultaneous orders)
            batchTimeoutRef.current = setTimeout(() => {
              const batch = pendingOrdersBatchRef.current;
              pendingOrdersBatchRef.current = [];
              batchTimeoutRef.current = null;
              
              const batchSize = batch.length;
              console.log(`📦 Processing batch of ${batchSize} order(s) received within ${BATCH_WINDOW_MS}ms`);
              
              // DON'T play audio here - will play after refresh completes
              // Just show toast immediately
              if (batchSize === 1) {
                // Single order: show "New order arrived"
                showToast('info', 'New Order', 'New order arrived');
              } else {
                // Multiple orders: show total pending count
                let pendingCount = batchSize; // Default to batch size
                if (getPendingOrdersCount && typeof getPendingOrdersCount === 'function') {
                  try {
                    const currentCount = getPendingOrdersCount();
                    if (typeof currentCount === 'number' && currentCount >= 0) {
                      // Add batch size since these orders just arrived
                      pendingCount = currentCount + batchSize;
                    }
                  } catch (err) {
                    console.warn('⚠️ Error getting pending orders count:', err);
                  }
                }
                showToast('info', 'New Orders', `${pendingCount} orders pending`);
              }
              
              // Call callback to refresh order list (audio will play after refresh)
              if (onNewOrder && typeof onNewOrder === 'function') {
                try {
                  // Only call once for the batch, not for each order
                  onNewOrder();
                } catch (err) {
                  console.warn('⚠️ Error in onNewOrder callback:', err);
                }
              }
              
              // Also show browser notification if tab is inactive (additional feedback)
              const isTabVisible = !document.hidden && isTabVisibleRef.current;
              if (!isTabVisible) {
                console.log('👁️ Tab is inactive - showing browser notification');
                showBrowserNotification('🆕 New Order Received!', {
                  body: batchSize === 1 ? 'You have a new order!' : `You have ${batchSize} new orders!`,
                  tag: 'new-order'
                });
              }
              
              // Broadcast to other tabs (only if this tab is active)
              if (isActiveTabRef.current) {
                broadcastNewOrder(batchSize);
              }
            }, 300); // 300ms delay to batch simultaneous orders
          }
        } catch (err) {
          console.error('❌ Error parsing WebSocket message:', err);
        }
      };

      socketRef.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setConnectionError('WebSocket connection error');
        setIsConnected(false);
        clearHeartbeat();
      };

      socketRef.current.onclose = (event) => {
        const wasClean = event.wasClean;
        const code = event.code;

        console.log(`🔌 WebSocket closed`, { wasClean, code, reason: event.reason });

        setIsConnected(false);
        clearHeartbeat();

        // Only reconnect if not a clean close
        if (!wasClean && code !== 1000 && code !== 1001) {
          showToast('warn', 'Disconnected', 'Disconnected from server. Reconnecting...');
          scheduleReconnect();
        }
      };
    } catch (err) {
      console.error('❌ Failed to create WebSocket:', err);
      setConnectionError(`Failed to create WebSocket: ${err.message}`);
      setIsConnected(false);
      scheduleReconnect();
    }
  }, [socketUrl, heartbeatInterval, heartbeatTimeout, startHeartbeat, clearHeartbeat, showToast]);

  /**
   * Schedule reconnection with exponential backoff (using setTimeout for background safety)
   */
  const scheduleReconnect = useCallback(() => {
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    reconnectAttemptsRef.current += 1;

    // Check if max attempts exceeded
    if (reconnectAttemptsRef.current > maxReconnectAttempts) {
      console.warn(`⚠️ Max reconnection attempts (${maxReconnectAttempts}) exceeded`);
      showToast('error', 'Connection Lost', 'Connection lost. Reloading page...', 5000);
      
      // Auto-reload page after 5 seconds
      reloadTimeoutRef.current = setTimeout(() => {
        console.log('🔄 Reloading page due to connection failure...');
        window.location.reload();
      }, 5000);
      return;
    }

    // Calculate backoff delay
    const delay = getBackoffDelay(reconnectAttemptsRef.current - 1);
    console.log(`🔄 Scheduling reconnect in ${delay}ms (Attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

    // Show reconnecting toast
    showToast('info', 'Reconnecting', `Reconnecting in ${Math.round(delay / 1000)} seconds...`, 3000);

    // Schedule reconnection using setTimeout (background-safe)
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connectWebSocket();
    }, delay);
  }, [maxReconnectAttempts, getBackoffDelay, connectWebSocket, showToast]);

  /**
   * Manual reconnect function
   */
  const reconnect = useCallback(() => {
    console.log('🔄 Manual reconnect requested');
    reconnectAttemptsRef.current = 0; // Reset attempts
    setConnectionError(null);
    
    // Clear any existing timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = null;
    }
    
    connectWebSocket();
  }, [connectWebSocket]);

  /**
   * Initialize WebSocket connection
   */
  useEffect(() => {
    if (!socketUrl) {
      console.warn('⚠️ WebSocket URL not provided');
      return;
    }

    // Request notification permission
    requestNotificationPermission();
    
    // Initialize BroadcastChannel (playOrderSound will be available via ref)
    initBroadcastChannel();
    
    connectWebSocket();

    // Cleanup
    return () => {
      console.log('🧹 Cleaning up WebSocket connection...');
      
      // Close BroadcastChannel
      if (broadcastChannelRef.current) {
        try {
          broadcastChannelRef.current.close();
          broadcastChannelRef.current = null;
        } catch (err) {
          console.warn('⚠️ Error closing BroadcastChannel:', err);
        }
      }
      
      // Clear all timers
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
      if (hideCloseButtonIntervalRef.current) {
        clearInterval(hideCloseButtonIntervalRef.current);
        hideCloseButtonIntervalRef.current = null;
      }
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }
      pendingOrdersBatchRef.current = []; // Clear pending batch
      clearHeartbeat();

      // Close WebSocket
      if (socketRef.current) {
        try {
          socketRef.current.close(1000, 'Component unmounting');
        } catch (err) {
          console.warn('⚠️ Error closing WebSocket:', err);
        }
        socketRef.current = null;
      }

      setIsConnected(false);
      setConnectionError(null);
    };
  }, [socketUrl, connectWebSocket, clearHeartbeat, requestNotificationPermission, initBroadcastChannel]);

  /**
   * Handle Page Visibility API - reconnection on tab visibility
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isTabVisibleRef.current = isVisible;

      if (isVisible) {
        console.log('👁️ Tab became visible');
        
        // Check WebSocket status and reconnect if needed
        if (!isConnected && socketRef.current?.readyState !== WebSocket.OPEN) {
          console.log('🔄 Tab visible - checking WebSocket connection...');
          // Reset attempts and reconnect
          reconnectAttemptsRef.current = 0;
          connectWebSocket();
        }

        // Resume AudioContext if needed (only if user has interacted)
        // AudioContext can only be resumed after user interaction
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          // Check if audio was previously unlocked (user has interacted)
          const wasUnlocked = loadUnlockState();
          if (wasUnlocked || audioUnlockedRef.current) {
            audioContextRef.current.resume().catch(err => {
              console.warn('⚠️ AudioContext resume failed (may need user interaction):', err);
              // Don't fail silently - mark as needing unlock
              audioUnlockedRef.current = false;
            });
          } else {
            console.log('ℹ️ AudioContext suspended - waiting for user interaction to resume');
          }
        }
      } else {
        console.log('👁️ Tab became hidden');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isConnected, connectWebSocket]);

  /**
   * Handle user interaction to resume AudioContext (required for autoplay policy)
   * This automatically resumes AudioContext when user interacts with the page,
   * preventing the "AudioContext was not allowed to start" warning
   */
  useEffect(() => {
    const handleUserInteraction = () => {
      // Resume AudioContext if it's suspended (only once per interaction)
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        console.log('👆 User interaction detected - resuming AudioContext...');
        audioContextRef.current.resume()
          .then(() => {
            console.log('✅ AudioContext resumed after user interaction');
            audioUnlockedRef.current = true;
            saveUnlockState(true);
          })
          .catch(err => {
            // Suppress autoplay warnings - they're expected
            if (err.name !== 'NotAllowedError' && !err.message.includes('user gesture')) {
              console.warn('⚠️ AudioContext resume failed:', err);
            }
          });
      }
    };

    // Listen for any user interaction (click, touch, keydown)
    // Use capture phase to catch interactions early
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: false, passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [saveUnlockState]);

  /**
   * Handle network online/offline events
   */
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Network connection restored');
      
      // Stop network disconnect audio when network comes back
      if (networkDisconnectAudioRef.current && networkDisconnectPlayingRef.current) {
        try {
          networkDisconnectAudioRef.current.pause();
          networkDisconnectAudioRef.current.currentTime = 0;
          networkDisconnectPlayingRef.current = false;
          console.log('🔇 Network disconnect audio stopped (network restored)');
        } catch (err) {
          console.warn('⚠️ Error stopping network disconnect audio:', err);
        }
      }
      
      // Stop the interval when network comes back (even before WebSocket connects)
      // This prevents unnecessary processing and ensures other toasts aren't affected
      stopHidingCloseButton();
      
      // Don't clear toast here - wait for WebSocket to actually connect
      // The toast will be cleared in onopen handler when WebSocket connects
      
      if (!isConnected && !reconnectTimeoutRef.current) {
        reconnectAttemptsRef.current = 0;
        setConnectionError(null);
        connectWebSocket();
      }
    };

    const handleOffline = () => {
      console.log('📴 Network connection lost');
      setConnectionError('No internet connection');
      
      // Play network disconnect audio (loops continuously until network comes back)
      if (networkDisconnectAudioRef.current) {
        // Only start playing if not already playing
        if (!networkDisconnectPlayingRef.current) {
          try {
            networkDisconnectPlayingRef.current = true;
            networkDisconnectAudioRef.current.currentTime = 0;
            networkDisconnectAudioRef.current.volume = 1.0;
            networkDisconnectAudioRef.current.muted = false;
            networkDisconnectAudioRef.current.loop = true; // Loop continuously
            
            const playPromise = networkDisconnectAudioRef.current.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('🔊 Network disconnect audio playing (will loop until network restored)');
                })
                .catch(err => {
                  console.warn('⚠️ Failed to play network disconnect audio:', err);
                  networkDisconnectPlayingRef.current = false;
                });
            }
          } catch (err) {
            console.warn('⚠️ Error playing network disconnect audio:', err);
            networkDisconnectPlayingRef.current = false;
          }
        } else {
          // Already playing - ensure it's still looping
          if (networkDisconnectAudioRef.current.paused) {
            try {
              networkDisconnectAudioRef.current.play();
              console.log('🔊 Resuming network disconnect audio');
            } catch (err) {
              console.warn('⚠️ Error resuming network disconnect audio:', err);
            }
          }
        }
      }
      
      // Only show toast if not already showing
      if (!networkErrorToastRef.current) {
        // Show persistent toast that won't hide until network reconnects (non-dismissible)
        networkErrorToastRef.current = showToast(
          'error', 
          'No Internet Connection', 
          'Internet connection lost. This message will disappear when connection is restored.',
          0, // 0 = sticky, won't auto-hide
          true, // persist until cleared
          false // NOT closable - user cannot dismiss it
        );
        networkErrorToastKeyRef.current = networkErrorToastRef.current;
        // Start continuously hiding close button
        startHidingCloseButton();
        console.log('📴 Network offline toast shown (non-dismissible)');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isConnected, connectWebSocket, showToast, clearToast, startHidingCloseButton, stopHidingCloseButton]);

  /**
   * Play order sound with throttling
   */
  const playOrderSound = useCallback(() => {
    // Skip audio if user role is 'user'
    if (userRole === 'user') {
      return;
    }
    // Update ref for BroadcastChannel
    playOrderSoundRef.current = playOrderSound;
    
    // CRITICAL: Check if audio element is initialized before proceeding
    // This prevents errors when WebSocket messages arrive before audio is ready
    if (!audioRef.current) {
      console.log('⏳ Audio element not initialized yet - will retry when ready');
      // Don't show toast here - home.js will handle it after orders refresh
      // Don't set isPlayingRef to true since we're not actually playing
      return;
    }
    
    console.log('🔊 playOrderSound() called', {
      audioReady,
      hasAudioRef: !!audioRef.current,
      audioReadyState: audioRef.current?.readyState,
      audioUnlocked: audioUnlockedRef.current,
      isPlaying: isPlayingRef.current
    });

    const now = Date.now();
    const timeSinceLastPlay = now - lastPlayTimeRef.current;

    // Industrial-grade deduplication: Prevent duplicate audio if:
    // 1. Audio is currently playing AND it's been less than 2 seconds (for simultaneous orders)
    // 2. Audio was just played less than 2 seconds ago (for rapid consecutive orders)
    const BATCH_WINDOW = 2000; // 2 seconds window to prevent duplicate audio
    if (isPlayingRef.current && timeSinceLastPlay < BATCH_WINDOW) {
      console.log(`⏭️ Sound already playing (${timeSinceLastPlay}ms ago), skipping duplicate - will batch with current order`);
      return;
    }
    
    if (!isPlayingRef.current && timeSinceLastPlay < BATCH_WINDOW) {
      console.log(`⏭️ Sound was just played (${timeSinceLastPlay}ms ago), skipping duplicate - orders are being batched`);
      return;
    }

    // Update timestamp and set playing flag
    lastPlayTimeRef.current = now;
    isPlayingRef.current = true;

    // ALWAYS try to unlock audio before playing (for missed orders, audio might not be unlocked)
    // If audio is not unlocked, we need to unlock it first, then play
    // IMPORTANT: We must wait for unlock to complete before attempting to play
    // to avoid the pause() from unlock interrupting the play() call
    const unlockAndPlay = async () => {
      if (audioRef.current) {
        // Check sessionStorage first
        const wasUnlocked = loadUnlockState();
        if (!wasUnlocked && !audioUnlockedRef.current) {
          console.log('🔓 Audio not unlocked - attempting unlock before playing...');
          // Try immediate unlock
          try {
            const unlockPromise = audioRef.current.play();
            if (unlockPromise !== undefined) {
              await unlockPromise;
              // Unlock successful - now pause and reset, then mark as unlocked
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              audioUnlockedRef.current = true;
              saveUnlockState(true);
              console.log('✅ Audio unlocked successfully before play');
              // Now proceed with actual play
              return true;
            } else {
              // Old browsers - play() returns undefined
              audioUnlockedRef.current = true;
              saveUnlockState(true);
              return true;
            }
          } catch (unlockErr) {
            console.warn('⚠️ Unlock attempt failed, will try to play anyway:', unlockErr);
            // Still try to play - might work if user has interacted
            return false;
          }
        } else if (wasUnlocked) {
          audioUnlockedRef.current = true;
          console.log('✅ Audio already unlocked (from sessionStorage)');
          return true;
        }
      }
      return true; // Already unlocked or no audio element
    };
    
    // Check if we need to unlock first
    const wasUnlocked = loadUnlockState();
    const needsUnlock = !wasUnlocked && !audioUnlockedRef.current;

    // Resume AudioContext if needed (only if user has interacted)
    // AudioContext can only be resumed after user interaction
    if (audioContextRef.current) {
      if (audioContextRef.current.state === 'suspended') {
        // Check if audio was previously unlocked (user has interacted)
        const wasUnlocked = loadUnlockState();
        if (wasUnlocked || audioUnlockedRef.current) {
          console.log('▶️ Resuming AudioContext...');
          audioContextRef.current.resume().then(() => {
            console.log('✅ AudioContext resumed');
          }).catch(err => {
            // Suppress autoplay warnings - they're expected behavior
            if (err.name !== 'NotAllowedError' && !err.message.includes('user gesture')) {
              console.warn('⚠️ AudioContext resume failed (may need user interaction):', err);
            } else {
              console.log('ℹ️ AudioContext resume requires user interaction (expected)');
            }
            // Don't fail silently - mark as needing unlock
            audioUnlockedRef.current = false;
          });
        } else {
          console.log('ℹ️ AudioContext suspended - waiting for user interaction to resume');
        }
      }
    }

    // Try to play audio - ALWAYS attempt, even if audioReady is false
    // Check if audio element exists and is ready (readyState >= HAVE_CURRENT_DATA = 2)
    const audioReadyToPlay = audioRef.current && (
      audioRef.current.readyState >= 2 || // HAVE_CURRENT_DATA or higher
      audioReady || // Or state says ready
      audioRef.current.readyState >= 1 // Or HAVE_METADATA (1) - can try to play
    );

    console.log('🎵 Audio ready check:', {
      audioReadyToPlay,
      readyState: audioRef.current?.readyState,
      audioReady,
      hasAudio: !!audioRef.current
    });

    if (audioReadyToPlay) {
      try {

        // Check if tab is inactive - try Web Audio API first (works better in background)
        // IMPORTANT: AudioContext must be resumed after user interaction
        const isTabInactive = document.hidden || !isTabVisibleRef.current;
        
        // Show browser notification if tab is inactive (additional feedback)
        if (isTabInactive) {
          console.log('👁️ Tab is inactive - showing browser notification (sound also playing)');
          showBrowserNotification('🆕 New Order Received!', {
            body: toastMessage,
            tag: 'new-order'
          });
        }
        
        // Try Web Audio API first if tab is inactive (works better in background tabs)
        // For inactive tabs, try Web Audio API but also try HTML5 Audio as fallback
        if (isTabInactive && audioContextRef.current && audioBufferRef.current) {
          // Check if AudioContext is suspended (needs user interaction)
          if (audioContextRef.current.state === 'suspended') {
            // Only try to resume if audio was previously unlocked (user has interacted)
            const wasUnlocked = loadUnlockState();
            if (wasUnlocked || audioUnlockedRef.current) {
              console.log('▶️ AudioContext is suspended - attempting to resume...');
              // Try to resume AudioContext (async, don't wait - HTML5 Audio will also be tried)
              audioContextRef.current.resume().then(() => {
                console.log('✅ AudioContext resumed - attempting Web Audio playback');
                try {
                  // Double-check state after resume
                  if (audioContextRef.current.state === 'running') {
                    const source = audioContextRef.current.createBufferSource();
                    const gainNode = audioContextRef.current.createGain();
                    
                    source.buffer = audioBufferRef.current;
                    gainNode.gain.value = 1.0;
                    
                    source.connect(gainNode);
                    gainNode.connect(audioContextRef.current.destination);
                    
                    source.onended = () => {
                      isPlayingRef.current = false;
                      source.disconnect();
                      gainNode.disconnect();
                      activeWebAudioSourcesRef.current.delete(source);
                    };
                    
                    activeWebAudioSourcesRef.current.add(source);
                    source.start(0);
                    console.log('✅ Order sound played using Web Audio API (background tab)');
                    audioUnlockedRef.current = true;
                    saveUnlockState(true);
                  } else {
                    console.warn('⚠️ AudioContext still not running after resume, HTML5 Audio will be used');
                  }
                } catch (webAudioError) {
                  console.warn('⚠️ Web Audio API playback failed, HTML5 Audio will be used:', webAudioError);
                }
              }).catch(resumeErr => {
                // Suppress the autoplay warning - it's expected behavior
                if (resumeErr.name !== 'NotAllowedError' && !resumeErr.message.includes('user gesture')) {
                  console.warn('⚠️ AudioContext resume failed, HTML5 Audio will be used:', resumeErr);
                } else {
                  console.log('ℹ️ AudioContext resume requires user interaction (expected), HTML5 Audio will be used');
                }
              });
            } else {
              console.log('ℹ️ AudioContext suspended - waiting for user interaction, HTML5 Audio will be used');
            }
          } else if (audioContextRef.current.state === 'running') {
            // AudioContext is running - try Web Audio API
            try {
              const source = audioContextRef.current.createBufferSource();
              const gainNode = audioContextRef.current.createGain();
              
              source.buffer = audioBufferRef.current;
              gainNode.gain.value = 1.0;
              
              source.connect(gainNode);
              gainNode.connect(audioContextRef.current.destination);
              
              source.onended = () => {
                isPlayingRef.current = false;
                source.disconnect();
                gainNode.disconnect();
                activeWebAudioSourcesRef.current.delete(source);
              };
              
              activeWebAudioSourcesRef.current.add(source);
              source.start(0);
              console.log('✅ Order sound played using Web Audio API (background tab)');
              audioUnlockedRef.current = true;
              saveUnlockState(true);
            } catch (webAudioError) {
              console.warn('⚠️ Web Audio API playback failed, HTML5 Audio will be used:', webAudioError);
            }
          } else {
            console.warn('⚠️ AudioContext in unexpected state:', audioContextRef.current.state, '- HTML5 Audio will be used');
          }
        }
        
        // Continue to HTML5 Audio (works in both active and inactive tabs)
        // If Web Audio already played, HTML5 Audio will be throttled or fail gracefully

        // Use HTML5 Audio - FORCE PLAY even if not fully ready
        console.log('🎵 Attempting to play HTML5 Audio...');
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1.0;
        audioRef.current.muted = false;

        // Force load if not loaded yet
        if (audioRef.current.readyState === 0) {
          console.log('🔄 Forcing audio load...');
          audioRef.current.load();
        }

        // Try to unlock and play
        // If we need to unlock, do it first and wait for it to complete
        const attemptPlay = async () => {
          // If audio needs unlocking, unlock first and wait
          if (needsUnlock) {
            const unlocked = await unlockAndPlay();
            if (!unlocked) {
              // Unlock failed - still try to play (might work)
              console.log('⚠️ Unlock failed but attempting play anyway...');
            }
            // Small delay to ensure unlock pause() has completed
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          
          // Now attempt to play
          try {
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('✅ Order sound played successfully!');
                  audioUnlockedRef.current = true;
                  saveUnlockState(true);
                  // Update ready state if not already set
                  if (!audioReady) {
                    setAudioReady(true);
                  }
                  // Reset playing flag after sound ends
                  audioRef.current.addEventListener('ended', () => {
                    isPlayingRef.current = false;
                  }, { once: true });
                })
                .catch((playError) => {
                  console.error('❌ Sound playback failed:', playError);
                  console.error('   Error name:', playError.name);
                  console.error('   Error message:', playError.message);
                  console.error('   Audio state:', {
                    readyState: audioRef.current?.readyState,
                    paused: audioRef.current?.paused,
                    muted: audioRef.current?.muted,
                    volume: audioRef.current?.volume
                  });
                  
                  // If autoplay blocked, try to unlock and play again
                  if (playError.name === 'NotAllowedError' || playError.name === 'NotSupportedError') {
                    console.log('🔓 Autoplay blocked, attempting unlock...');
                    audioUnlockedRef.current = false;
                    unlockAudio();
                    // Try once more after a short delay
                    setTimeout(() => {
                      if (audioRef.current && !isPlayingRef.current) {
                        console.log('🔄 Retrying audio play after unlock...');
                        audioRef.current.play()
                          .then(() => {
                            console.log('✅ Audio played after unlock!');
                            audioUnlockedRef.current = true;
                            saveUnlockState(true);
                          })
                          .catch(retryError => {
                            console.error('❌ Retry also failed:', retryError);
                            isPlayingRef.current = false;
                            // Don't show toast - home.js will handle it after orders refresh
                          });
                      }
                    }, 100);
                  } else if (playError.name === 'AbortError') {
                    // AbortError means play() was interrupted - this can happen if unlock pause() interrupted it
                    // Wait a bit and retry
                    console.log('⚠️ Play interrupted (AbortError) - retrying after delay...');
                    setTimeout(() => {
                      if (audioRef.current && !isPlayingRef.current) {
                        audioRef.current.play()
                          .then(() => {
                            console.log('✅ Audio played after retry!');
                            audioUnlockedRef.current = true;
                            saveUnlockState(true);
                            audioRef.current.addEventListener('ended', () => {
                              isPlayingRef.current = false;
                            }, { once: true });
                          })
                          .catch(retryError => {
                            console.error('❌ Retry after AbortError failed:', retryError);
                            isPlayingRef.current = false;
                            // Don't show toast - home.js will handle it after orders refresh
                          });
                      }
                    }, 100);
                  } else {
                    // Other errors - reset
                    isPlayingRef.current = false;
                    // Don't show toast - home.js will handle it after orders refresh
                  }
                });
            } else {
              // Old browsers - play() returns undefined
              console.log('✅ Audio played (legacy browser)');
              audioUnlockedRef.current = true;
              saveUnlockState(true);
              if (!audioReady) {
                setAudioReady(true);
              }
            }
          } catch (err) {
            console.error('❌ Error in attemptPlay:', err);
            isPlayingRef.current = false;
            // Don't show toast - home.js will handle it after orders refresh
          }
        };

        // Attempt to play (async)
        attemptPlay();
      } catch (err) {
        console.error('❌ Error playing sound:', err);
        isPlayingRef.current = false;
        // Don't show toast - home.js will handle it after orders refresh
      }
    } else if (audioRef.current) {
      // Audio element exists but not ready - try to play anyway
      console.log('⚠️ Audio not fully ready (readyState:', audioRef.current.readyState, '), but attempting to play anyway...');
      try {
        audioRef.current.load(); // Force load
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1.0;
        audioRef.current.muted = false;
        
        // Wait for canplay event if needed
        const tryPlay = () => {
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('✅ Order sound played successfully (forced)');
                audioUnlockedRef.current = true;
                saveUnlockState(true);
                setAudioReady(true);
              })
              .catch((playError) => {
                console.error('⚠️ Forced play failed:', playError);
                console.error('   Error:', playError.name, playError.message);
                isPlayingRef.current = false;
                // Don't show toast - home.js will handle it after orders refresh
              });
          } else {
            audioUnlockedRef.current = true;
            saveUnlockState(true);
            setAudioReady(true);
          }
        };

        if (audioRef.current.readyState >= 2) {
          tryPlay();
        } else {
          // Wait for canplay event
          const onCanPlay = () => {
            audioRef.current.removeEventListener('canplay', onCanPlay);
            tryPlay();
          };
          audioRef.current.addEventListener('canplay', onCanPlay);
          // Timeout after 2 seconds
          setTimeout(() => {
            audioRef.current.removeEventListener('canplay', onCanPlay);
            if (audioRef.current.readyState >= 2) {
              tryPlay();
            } else {
              console.warn('⚠️ Audio still not ready after timeout');
              isPlayingRef.current = false;
              // Don't show toast - home.js will handle it after orders refresh
            }
          }, 2000);
        }
      } catch (err) {
        console.error('❌ Error in forced audio play:', err);
        isPlayingRef.current = false;
        // Don't show toast - home.js will handle it after orders refresh
      }
    } else {
      // No audio element at all
      // This should rarely happen now since we check at the start of the function
      // But keep it as a safety net
      console.log('ℹ️ Audio element not available');
      console.log('   audioRef.current:', audioRef.current);
      console.log('   audioReady:', audioReady);
      isPlayingRef.current = false;
      // Don't show toast - home.js will handle it after orders refresh
    }
    
    // Broadcast to other tabs when playing sound (only if this tab is active)
    // Note: Sound now plays in all tabs (active and inactive), but we only broadcast from active tab
    if (isActiveTabRef.current) {
      broadcastNewOrder(1);
    }
  }, [audioReady, throttleMs, toastMessage, showToast, unlockAudio, saveUnlockState, loadUnlockState, showBrowserNotification, broadcastNewOrder, userRole]);

  // Memoize error to prevent unnecessary re-renders
  const error = useMemo(() => {
    return audioError || connectionError || null;
  }, [audioError, connectionError]);

  return {
    /**
     * Play order sound notification
     */
    playOrderSound,
    
    /**
     * Audio system ready state
     */
    ready: audioReady,
    
    /**
     * Error state (audio or connection)
     */
    error,
    
    /**
     * WebSocket connection status
     */
    isConnected,
    
    /**
     * Manual reconnect function
     */
    reconnect,
    
    /**
     * Audio unlock function
     */
    unlockAudio,
    
    /**
     * Current reconnection attempt count
     */
    reconnectAttempts: reconnectAttemptsRef.current,
  };
};

export default useOrderSoundNotification;
